import type { TEvent } from "../types";

/**
 * EventPublisher class responsible for publishing events to subscribers
 */
class EventPublisher {
  private static instance: EventPublisher;
  private subscribers: Array<(event: TEvent) => void> = [];

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get the singleton instance of EventPublisher
   */
  public static getInstance(): EventPublisher {
    if (!EventPublisher.instance) {
      EventPublisher.instance = new EventPublisher();
    }
    return EventPublisher.instance;
  }

  /**
   * Subscribe to events
   * @param callback Function to be called when an event is published
   * @returns Unsubscribe function
   */
  public subscribe(callback: (event: TEvent) => void): () => void {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(
        (subscriber) => subscriber !== callback,
      );
    };
  }

  /**
   * Publish an event to all subscribers
   * @param event The event to publish
   */
  public publish(event: TEvent): void {
    try {
      console.log("EventPublisher: Received event", {
        type: event.type,
        data: event
      });

      // Add timestamp if not already present
      const eventWithTimestamp = !event.timestamp
        ? { ...event, timestamp: Date.now() }
        : event;

      console.log("EventPublisher: Number of subscribers:", this.subscribers.length);

      // Notify all subscribers
      for (const subscriber of this.subscribers) {
        try {
          console.log("EventPublisher: Notifying subscriber");
          subscriber(eventWithTimestamp);
          console.log("EventPublisher: Subscriber notified successfully");
        } catch (error) {
          console.error("EventPublisher: Error in subscriber:", error);
          // Continue with other subscribers even if one fails
        }
      }

      console.log("EventPublisher: Event processing completed");
    } catch (error) {
      console.error("Error publishing event:", error, event);
      // Silently fail to prevent app crashes due to event publishing issues
    }
  }
}

// Export singleton instance
export const eventPublisher = EventPublisher.getInstance();

/**
 * Helper function to publish events
 * @param event The event to publish
 */
export function publishEvent(event: TEvent): void {
  try {
    eventPublisher.publish(event);
  } catch (error) {
    console.error("Error in publishEvent helper:", error);
    // Silently fail to prevent app crashes due to event publishing issues
  }
}
